import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, Download, ExternalLink, CheckCircle, Clock, XCircle, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InvoiceList = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadInvoices();
  }, []);

  const loadInvoices = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/subscription/invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoices(response.data.invoices || []);
    } catch (error) {
      console.error('Error loading invoices:', error);
      toast.error('Fehler beim Laden der Rechnungen');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      paid: {
        label: 'Bezahlt',
        color: 'bg-green-100 text-green-800',
        icon: CheckCircle
      },
      open: {
        label: 'Offen',
        color: 'bg-yellow-100 text-yellow-800',
        icon: Clock
      },
      void: {
        label: 'Storniert',
        color: 'bg-gray-100 text-gray-800',
        icon: XCircle
      },
      uncollectible: {
        label: 'Nicht einziehbar',
        color: 'bg-red-100 text-red-800',
        icon: AlertCircle
      }
    };

    const config = statusConfig[status] || statusConfig.open;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${config.color}`}>
        <Icon className="w-3.5 h-3.5" />
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatPeriod = (startDate, endDate, isSubscription) => {
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      
      const startMonth = start.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
      const endMonth = end.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
      
      if (startMonth === endMonth) {
        return startMonth;
      }
      
      return `${start.toLocaleDateString('de-DE', { month: 'short' })} – ${end.toLocaleDateString('de-DE', { month: 'short', year: 'numeric' })}`;
    }
    
    // If no period dates but it's a subscription invoice, show "Wiederkehrend"
    if (isSubscription) {
      return 'Wiederkehrend';
    }
    
    // Truly one-time payment
    return 'Einmalig';
  };

  const formatAmount = (amount, currency) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: currency || 'EUR'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-3xl shadow-xl p-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="space-y-4">
            <div className="h-16 bg-gray-100 rounded"></div>
            <div className="h-16 bg-gray-100 rounded"></div>
            <div className="h-16 bg-gray-100 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (invoices.length === 0) {
    return (
      <div className="bg-white rounded-3xl shadow-xl p-8">
        <div className="flex items-center gap-3 mb-6">
          <FileText className="w-6 h-6 text-[#2B2142]" />
          <h2 className="text-2xl font-bold text-gray-900">Rechnungen</h2>
        </div>
        
        <div className="text-center py-12">
          <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600">Noch keine Rechnungen vorhanden.</p>
          <p className="text-sm text-gray-500 mt-2">
            Deine Rechnungen werden hier angezeigt, sobald Zahlungen verarbeitet wurden.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-3xl shadow-xl p-8">
      <div className="flex items-center gap-3 mb-6">
        <FileText className="w-6 h-6 text-[#2B2142]" />
        <h2 className="text-2xl font-bold text-gray-900">Rechnungen</h2>
      </div>

      <div className="mb-6 p-4 bg-[#FFF0D1]/30 border border-[#2B2142]/20 rounded-xl">
        <p className="text-sm text-[#2B2142]">
          💡 Hier findest du alle Rechnungen für deine Buchhaltung. 
          Klicke auf "PDF herunterladen", um die Rechnung zu speichern.
        </p>
      </div>

      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b-2 border-gray-200">
              <th className="text-left py-4 px-4 font-semibold text-gray-700">Rechnungsnr.</th>
              <th className="text-left py-4 px-4 font-semibold text-gray-700">Datum</th>
              <th className="text-left py-4 px-4 font-semibold text-gray-700">Zeitraum</th>
              <th className="text-left py-4 px-4 font-semibold text-gray-700">Plan</th>
              <th className="text-right py-4 px-4 font-semibold text-gray-700">Betrag</th>
              <th className="text-center py-4 px-4 font-semibold text-gray-700">Status</th>
              <th className="text-center py-4 px-4 font-semibold text-gray-700">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((invoice) => (
              <tr key={invoice.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                <td className="py-4 px-4">
                  <span className="font-mono text-sm text-gray-600">{invoice.number}</span>
                </td>
                <td className="py-4 px-4 text-gray-700">
                  {formatDate(invoice.date)}
                </td>
                <td className="py-4 px-4 text-gray-600 text-sm">
                  {formatPeriod(invoice.period_start, invoice.period_end, invoice.is_subscription)}
                </td>
                <td className="py-4 px-4">
                  <span className="text-gray-700">
                    {invoice.plan_name || invoice.description || 'EventConnect'}
                  </span>
                </td>
                <td className="py-4 px-4 text-right font-semibold text-gray-900">
                  {formatAmount(invoice.amount, invoice.currency)}
                </td>
                <td className="py-4 px-4 text-center">
                  {getStatusBadge(invoice.status)}
                </td>
                <td className="py-4 px-4">
                  <div className="flex items-center justify-center gap-2">
                    {invoice.pdf_url && (
                      <a
                        href={invoice.pdf_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-[#2B2142] hover:bg-[#1E1730] text-white text-sm font-semibold rounded-lg transition-colors"
                      >
                        <Download className="w-4 h-4" />
                        PDF
                      </a>
                    )}
                    {invoice.hosted_url && (
                      <a
                        href={invoice.hosted_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-semibold rounded-lg transition-colors"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Ansehen
                      </a>
                    )}
                    {!invoice.pdf_url && !invoice.hosted_url && (
                      <span className="text-sm text-gray-400">Nicht verfügbar</span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden space-y-4">
        {invoices.map((invoice) => (
          <div key={invoice.id} className="border border-gray-200 rounded-2xl p-4">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="font-mono text-sm text-gray-600 mb-1">{invoice.number}</div>
                <div className="text-sm text-gray-500">{formatDate(invoice.date)}</div>
              </div>
              {getStatusBadge(invoice.status)}
            </div>

            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Zeitraum:</span>
                <span className="font-medium text-gray-900">
                  {formatPeriod(invoice.period_start, invoice.period_end, invoice.is_subscription)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Plan:</span>
                <span className="font-medium text-gray-900">
                  {invoice.plan_name || invoice.description || 'EventConnect'}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Betrag:</span>
                <span className="font-semibold text-gray-900">
                  {formatAmount(invoice.amount, invoice.currency)}
                </span>
              </div>
            </div>

            <div className="flex gap-2">
              {invoice.pdf_url && (
                <a
                  href={invoice.pdf_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-[#2B2142] hover:bg-[#1E1730] text-white text-sm font-semibold rounded-lg transition-colors"
                >
                  <Download className="w-4 h-4" />
                  PDF
                </a>
              )}
              {invoice.hosted_url && (
                <a
                  href={invoice.hosted_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-semibold rounded-lg transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  Ansehen
                </a>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-xl">
        <p className="text-sm text-gray-600">
          📄 <strong>Hinweis:</strong> Alle Rechnungen werden automatisch generiert und können für deine Buchhaltung verwendet werden. 
          Bei Fragen wende dich an{' '}
          <a href="mailto:kontakt@evconn.de" className="text-[#2B2142] hover:underline">
            kontakt@evconn.de
          </a>.
        </p>
      </div>
    </div>
  );
};

export default InvoiceList;
